import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { setPath } from '@/lib/setSlug';

const SITE = 'https://lorescout.com';

interface SetRow {
  id: string;
  name: string;
  code: string;
  released_at: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'TBA';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function RotationPage() {
  const { data: sets } = useQuery({
    queryKey: ['rotation-sets'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sets')
        .select('id,name,code,released_at')
        .not('released_at', 'is', null)
        .order('released_at', { ascending: true });
      if (error) throw error;
      // Numeric sets only (filter out promos like P1, D23, cp)
      return (data || []).filter(s => /^\d+$/.test(s.code)) as SetRow[];
    },
  });

  const today = new Date();
  const released = (sets || []).filter(s => s.released_at && new Date(s.released_at) <= today);
  const upcoming = (sets || []).filter(s => s.released_at && new Date(s.released_at) > today);

  // Lorcana Core format: latest 4 numbered sets are Core-legal; older ones drop into Archive on each major set release.
  const core = released.slice(-4);
  const archive = released.slice(0, Math.max(0, released.length - 4));

  const description = `Disney Lorcana rotation — the current Core format includes ${core.map(s => s.name).join(', ')}. Older sets rotate to Archive. Updated set-by-set on LoreScout.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does Lorcana rotation work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Disney Lorcana uses a rolling "Core" format that includes the most recent numbered sets. When a new numbered set releases, the oldest set rotates out of Core and into the Archive format, where every legal card ever printed remains playable.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which sets are currently legal in Core?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `The current Core format includes: ${core.map(s => `${s.name} (Set ${s.code})`).join(', ')}.`,
        },
      },
    ],
  };

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>Lorcana Rotation — Current Core Format & Archive · LoreScout</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE}/rotation`} />
        <meta property="og:title" content="Disney Lorcana Rotation — Current Core Format" />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SITE}/rotation`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}<span className="text-foreground">Rotation</span>
        </nav>
        <h1 className="font-serif text-3xl">Lorcana Rotation</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Disney Lorcana uses a rolling <strong>Core</strong> format. When a new numbered set releases, the oldest set
          rotates out of Core and into <strong>Archive</strong>, where it remains playable.
        </p>

        <section className="mt-6">
          <h2 className="font-serif text-xl mb-3">Current Core sets</h2>
          <ul className="space-y-2">
            {core.map(s => (
              <li key={s.id}>
                <Link to={setPath(s)} className="flex items-center justify-between rounded-md border border-primary/40 bg-primary/5 px-3 py-2 hover:bg-primary/10">
                  <span>
                    <span className="font-medium">{s.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">Set {s.code}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDate(s.released_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {!!upcoming.length && (
          <section className="mt-8">
            <h2 className="font-serif text-xl mb-3">Upcoming (will enter Core)</h2>
            <ul className="space-y-2">
              {upcoming.map(s => (
                <li key={s.id}>
                  <Link to={setPath(s)} className="flex items-center justify-between rounded-md border border-border px-3 py-2 hover:bg-muted/40">
                    <span>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">Set {s.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(s.released_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!!archive.length && (
          <section className="mt-8">
            <h2 className="font-serif text-xl mb-3">Archive (rotated out of Core)</h2>
            <ul className="space-y-2">
              {archive.map(s => (
                <li key={s.id}>
                  <Link to={setPath(s)} className="flex items-center justify-between rounded-md border border-border px-3 py-2 hover:bg-muted/40 opacity-90">
                    <span>
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">Set {s.code}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">{formatDate(s.released_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10 rounded-lg border border-border p-4 text-sm text-muted-foreground">
          <p>
            Note: LoreScout groups Core based on the four most recent numbered sets. Always confirm tournament-legal
            sets with Ravensburger's official rules before competitive play.
          </p>
        </section>
      </article>
    </div>
  );
}
